import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { BookOpen, Upload, Trash2, GripVertical, Eye, EyeOff, Film, ImageIcon, Loader2 } from "lucide-react";
import { Lookbook, lookbookFileUrl, type LookbookFlipStyle, type LookbookItem } from "@/components/Lookbook";

const FLIP_STYLES: { value: LookbookFlipStyle; label: string; description: string }[] = [
  { value: "curl", label: "3D page curl", description: "Realistic page-turn — premium, slightly heavier." },
  { value: "slide", label: "Slide flip", description: "Clean swipe with a subtle perspective tilt." },
  { value: "fade", label: "Fade + lift", description: "Minimal cross-fade with a soft lift." },
];

export const LookbookManagerCard = () => {
  const { toast } = useToast();
  const [items, setItems] = useState<LookbookItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [flipStyle, setFlipStyle] = useState<LookbookFlipStyle>("curl");
  const [savingStyle, setSavingStyle] = useState(false);
  const [dragId, setDragId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [showOnHome, setShowOnHome] = useState(false);
  const [savingShow, setSavingShow] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    const [itemsRes, settingRes, showRes] = await Promise.all([
      supabase
        .from("lookbook_items")
        .select("*")
        .order("sort_order", { ascending: true })
        .order("created_at", { ascending: true }),
      supabase
        .from("site_settings")
        .select("value")
        .eq("key", "lookbook_flip_style")
        .maybeSingle(),
      supabase
        .from("site_settings")
        .select("value")
        .eq("key", "lookbook_show_on_home")
        .maybeSingle(),
    ]);
    if (itemsRes.data) setItems(itemsRes.data as LookbookItem[]);
    const v = (settingRes.data?.value ?? "curl") as LookbookFlipStyle;
    if (v === "curl" || v === "slide" || v === "fade") setFlipStyle(v);
    setShowOnHome(showRes.data?.value === "true");
    setLoading(false);
  };


  useEffect(() => {
    fetchAll();
  }, []);

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    let nextOrder = items.length > 0 ? Math.max(...items.map((i) => i.sort_order)) + 1 : 0;
    for (const file of Array.from(files)) {
      const isVideo = file.type.startsWith("video/");
      const isImage = file.type.startsWith("image/");
      if (!isVideo && !isImage) {
        toast({ title: "Skipped", description: `${file.name} is not an image or video.`, variant: "destructive" });
        continue;
      }
      // 50MB cap to avoid surprises
      if (file.size > 50 * 1024 * 1024) {
        toast({ title: "Too large", description: `${file.name} is over 50MB.`, variant: "destructive" });
        continue;
      }
      const ext = file.name.split(".").pop() || (isVideo ? "mp4" : "jpg");
      const path = `${crypto.randomUUID()}.${ext}`;
      const { error: upErr } = await supabase.storage.from("lookbook").upload(path, file, {
        cacheControl: "3600",
        contentType: file.type,
      });
      if (upErr) {
        toast({ title: "Upload failed", description: upErr.message, variant: "destructive" });
        continue;
      }
      const { error: insErr } = await supabase.from("lookbook_items").insert({
        file_path: path,
        media_type: isVideo ? "video" : "image",
        caption: "",
        sort_order: nextOrder++,
        is_visible: true,
      });
      if (insErr) {
        toast({ title: "Save failed", description: insErr.message, variant: "destructive" });
      }
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
    await fetchAll();
    toast({ title: "Uploaded", description: "Lookbook updated." });
  };

  const updateItem = async (id: string, patch: Partial<LookbookItem>) => {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...patch } : i)));
    const { error } = await supabase.from("lookbook_items").update(patch).eq("id", id);
    if (error) toast({ title: "Save failed", description: error.message, variant: "destructive" });
  };

  const removeItem = async (item: LookbookItem) => {
    if (!confirm("Remove this lookbook page?")) return;
    const { error } = await supabase.from("lookbook_items").delete().eq("id", item.id);
    if (error) {
      toast({ title: "Delete failed", description: error.message, variant: "destructive" });
      return;
    }
    await supabase.storage.from("lookbook").remove([item.file_path]);
    setItems((prev) => prev.filter((i) => i.id !== item.id));
  };

  const persistOrder = async (ordered: LookbookItem[]) => {
    setItems(ordered);
    // Update sort_order one by one (small list, simple)
    await Promise.all(
      ordered.map((it, idx) =>
        supabase.from("lookbook_items").update({ sort_order: idx }).eq("id", it.id),
      ),
    );
  };

  const onDragStart = (id: string) => () => setDragId(id);
  const onDragOver = (id: string) => (e: React.DragEvent) => {
    e.preventDefault();
    setDragOverId(id);
  };
  const onDrop = (id: string) => (e: React.DragEvent) => {
    e.preventDefault();
    if (!dragId || dragId === id) {
      setDragId(null);
      setDragOverId(null);
      return;
    }
    const fromIdx = items.findIndex((i) => i.id === dragId);
    const toIdx = items.findIndex((i) => i.id === id);
    if (fromIdx === -1 || toIdx === -1) return;
    const next = [...items];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    setDragId(null);
    setDragOverId(null);
    persistOrder(next);
  };

  const saveFlipStyle = async (value: LookbookFlipStyle) => {
    setFlipStyle(value);
    setSavingStyle(true);
    const { data: existing } = await supabase
      .from("site_settings")
      .select("id")
      .eq("key", "lookbook_flip_style")
      .maybeSingle();
    if (existing) {
      await supabase
        .from("site_settings")
        .update({ value, updated_at: new Date().toISOString() })
        .eq("key", "lookbook_flip_style");
    } else {
      await supabase.from("site_settings").insert({ key: "lookbook_flip_style", value });
    }
    setSavingStyle(false);
    toast({ title: "Saved", description: "Flip style updated." });
  };

  const saveShowOnHome = async (next: boolean) => {
    setShowOnHome(next);
    setSavingShow(true);
    const value = next ? "true" : "false";
    const { data: existing } = await supabase
      .from("site_settings")
      .select("id")
      .eq("key", "lookbook_show_on_home")
      .maybeSingle();
    if (existing) {
      await supabase
        .from("site_settings")
        .update({ value, updated_at: new Date().toISOString() })
        .eq("key", "lookbook_show_on_home");
    } else {
      await supabase.from("site_settings").insert({ key: "lookbook_show_on_home", value });
    }
    setSavingShow(false);
    toast({
      title: "Saved",
      description: next ? "Lookbook is now live on the homepage." : "Lookbook hidden from the homepage.",
    });
  };


  const visibleItems = items.filter((i) => i.is_visible);

  return (
    <Card className="bg-card border-border/50 max-w-4xl">
      <CardHeader>
        <CardTitle className="text-sm font-light tracking-[0.3em] uppercase text-muted-foreground flex items-center gap-2 flex-wrap">
          <BookOpen size={16} />
          <span>Lookbook</span>
          <Badge
            variant="outline"
            className="ml-auto normal-case tracking-normal text-[10px] font-medium bg-primary/10 text-primary border-primary/30"
          >
            {items.length} page{items.length === 1 ? "" : "s"}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <p className="text-sm text-muted-foreground font-light leading-relaxed">
          A flip-book of your work shown under the Instagram feed on the homepage. Add photos or short videos,
          drag to reorder, and pick a flip animation.
        </p>

        {/* Show on homepage */}
        <div className="flex items-center justify-between gap-4 border border-border/50 p-4">
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Show on homepage</Label>
            <p className="text-[11px] text-muted-foreground font-light">
              {showOnHome
                ? "The lookbook section is live on the homepage."
                : "Hidden — visitors won't see the lookbook section."}
              {savingShow && " Saving…"}
            </p>
          </div>
          <Switch checked={showOnHome} onCheckedChange={saveShowOnHome} disabled={savingShow} />
        </div>


        {/* Flip style selector */}
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Flip animation</Label>
          <Select value={flipStyle} onValueChange={(v) => saveFlipStyle(v as LookbookFlipStyle)}>
            <SelectTrigger className="max-w-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FLIP_STYLES.map((s) => (
                <SelectItem key={s.value} value={s.value}>
                  <div className="flex flex-col">
                    <span className="text-sm">{s.label}</span>
                    <span className="text-[11px] text-muted-foreground">{s.description}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {savingStyle && (
            <p className="text-[11px] text-muted-foreground flex items-center gap-1">
              <Loader2 size={10} className="animate-spin" /> Saving…
            </p>
          )}
        </div>

        {/* Upload */}
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Add pages</Label>
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*"
              multiple
              hidden
              onChange={(e) => handleFiles(e.target.files)}
            />
            <Button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="text-xs tracking-widest uppercase gap-2"
            >
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              {uploading ? "Uploading…" : "Upload images / videos"}
            </Button>
            <span className="text-[11px] text-muted-foreground">JPG, PNG, MP4, WebM · up to 50MB each</span>
          </div>
        </div>

        {/* Live preview */}
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Live preview</Label>
          <div className="rounded-md border border-border/40 bg-secondary/30 p-6">
            <Lookbook items={visibleItems} flipStyle={flipStyle} compact />
            {visibleItems.length === 0 && items.length > 0 && (
              <p className="text-[11px] text-center text-muted-foreground mt-3">
                All pages are hidden. Toggle visibility below to preview.
              </p>
            )}
          </div>
        </div>

        {/* Items list */}
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Pages (drag to reorder)</Label>
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">No pages yet — upload your first one above.</p>
          ) : (
            <ul className="space-y-2">
              {items.map((item) => (
                <li
                  key={item.id}
                  draggable
                  onDragStart={onDragStart(item.id)}
                  onDragOver={onDragOver(item.id)}
                  onDrop={onDrop(item.id)}
                  onDragEnd={() => {
                    setDragId(null);
                    setDragOverId(null);
                  }}
                  className={`flex items-center gap-3 p-2 rounded-md border bg-background transition-colors ${
                    dragOverId === item.id ? "border-primary/60 bg-primary/5" : "border-border/40"
                  } ${dragId === item.id ? "opacity-50" : ""}`}
                >
                  <GripVertical size={16} className="text-muted-foreground cursor-grab shrink-0" />
                  <div className="w-14 h-14 rounded overflow-hidden bg-muted shrink-0 relative">
                    {item.media_type === "video" ? (
                      <>
                        <video
                          src={lookbookFileUrl(item.file_path)}
                          className="w-full h-full object-cover"
                          muted
                          playsInline
                        />
                        <Film size={12} className="absolute bottom-0.5 right-0.5 text-white drop-shadow" />
                      </>
                    ) : (
                      <>
                        <img
                          src={lookbookFileUrl(item.file_path)}
                          alt={item.caption || "Lookbook page"}
                          className="w-full h-full object-cover"
                        />
                        <ImageIcon size={12} className="absolute bottom-0.5 right-0.5 text-white drop-shadow" />
                      </>
                    )}
                  </div>
                  <Input
                    value={item.caption}
                    placeholder="Caption (optional)"
                    onChange={(e) =>
                      setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, caption: e.target.value } : i)))
                    }
                    onBlur={(e) => updateItem(item.id, { caption: e.target.value })}
                    className="h-9 text-sm flex-1 min-w-0"
                  />
                  <button
                    type="button"
                    onClick={() => updateItem(item.id, { is_visible: !item.is_visible })}
                    title={item.is_visible ? "Hide from public" : "Show on public site"}
                    className={`shrink-0 p-2 rounded border transition-colors ${
                      item.is_visible
                        ? "border-emerald-500/30 text-emerald-600 hover:bg-emerald-500/10"
                        : "border-border/40 text-muted-foreground hover:bg-muted"
                    }`}
                  >
                    {item.is_visible ? <Eye size={14} /> : <EyeOff size={14} />}
                  </button>
                  <button
                    type="button"
                    onClick={() => removeItem(item)}
                    title="Delete"
                    className="shrink-0 p-2 rounded border border-destructive/30 text-destructive hover:bg-destructive/10 transition-colors"
                  >
                    <Trash2 size={14} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
